import { JSX, useEffect } from 'react';
import {
  Controller,
  FieldArrayWithId,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import { AddActionButton } from 'components/AddActionButton';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import {
  ChargeInputCard,
  ChargeTypeOption,
} from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/components/CostItemInputCard';
import { currencyList } from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/constants';
import { LoadBuildingTextInput } from 'pages/QuoteView/LoadBuilding/components/LoadBuildingTextInput';
import { CostLineItem, NormalizedLoad, Unit } from 'types/Load';
import { cn } from 'utils/shadcn';

interface LineItemSectionProps {
  title: string;
  formMethods: UseFormReturn<NormalizedLoad>;
  lineItemsFieldPath:
    'rateData.customerLineItems' | 'rateData.carrierLineItems';
  // Omit when the line items are only part of the total (the TMS adds line haul and fuel), so
  // the section doesn't overwrite it with their sum.
  totalFieldPath?: 'rateData.customerTotalCharge' | 'rateData.carrierTotalCost';
  fields: FieldArrayWithId<NormalizedLoad, any, 'id'>[];
  onAddCharge: () => void;
  onRemoveCharge: (index: number) => void;
  watchedLineItems: CostLineItem[];
  watchedTotal?: number;
  chargeTypeOptions?: ChargeTypeOption[];
  addChargeDescription?: string;
  readOnly?: boolean;
  hideRateQualifier?: boolean;
}

export function LineItemSection({
  title,
  formMethods,
  lineItemsFieldPath,
  totalFieldPath,
  fields,
  onAddCharge,
  onRemoveCharge,
  watchedLineItems,
  watchedTotal,
  chargeTypeOptions,
  addChargeDescription = 'Add charges like freight, fuel, and accessorials.',
  readOnly = false,
  hideRateQualifier = false,
}: LineItemSectionProps): JSX.Element {
  const { control, setValue } = formMethods;
  const watchedCurrencyUnit = useWatch({
    control,
    name: totalFieldPath
      ? `${totalFieldPath}.unit`
      : 'rateData.customerTotalCharge.unit',
    disabled: !totalFieldPath,
  });

  // Calculate total line items charge
  const totalLineItems =
    watchedLineItems?.reduce(
      (sum, charge) => sum + (charge.totalChargeUSD || 0),
      0
    ) || 0;

  // Auto-calculate the total charge
  useEffect(() => {
    if (!totalFieldPath) {
      return;
    }

    if (totalLineItems !== watchedTotal) {
      setValue(`${totalFieldPath}.val`, totalLineItems, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    // Turvo supports USD only, so keep currency aligned with calculated totals.
    if (!watchedCurrencyUnit) {
      setValue(`${totalFieldPath}.unit`, Unit.USD, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [
    totalLineItems,
    watchedTotal,
    watchedCurrencyUnit,
    setValue,
    totalFieldPath,
  ]);

  return (
    <>
      {/* Section Title */}
      <Typography
        variant='h6'
        className='col-span-1 text-md text-neutral-800 font-semibold mb-1'
      >
        {title}
      </Typography>

      {/* Line Items Section */}
      <div
        className={cn(
          fields.length > 0 && 'space-y-2',
          fields.length === 0 && 'hidden'
        )}
      >
        {fields.map((field, index) => (
          <ChargeInputCard
            key={field.id}
            index={index}
            formMethods={formMethods}
            onRemove={onRemoveCharge}
            fieldPath={lineItemsFieldPath}
            typeOptions={chargeTypeOptions}
            readOnly={readOnly}
            hideRateQualifier={hideRateQualifier}
          />
        ))}
      </div>

      {!readOnly && (
        <AddActionButton
          onClick={onAddCharge}
          buttonText='Add Charge'
          descriptionText={addChargeDescription}
          buttonNamePosthog='add_line_item'
        />
      )}

      {totalFieldPath && (
        <Grid cols='2' gap='sm' className='mx-0 w-full'>
          <LoadBuildingTextInput
            name={`${totalFieldPath}.val`}
            label={`Total Amount`}
            hideAIHint
            inputType='number'
            options={{ valueAsNumber: true }}
            required={false}
            readOnly={true}
            inputClassName='no-spin-buttons'
          />

          <Flex direction='col' className='col-span-1'>
            <Label name={`${totalFieldPath}.unit`} required={false} hideAIHint>
              Currency
            </Label>
            <Controller
              name={`${totalFieldPath}.unit`}
              control={control}
              render={({ field }) => (
                <Select
                  showSearch
                  className='h-8'
                  placeholder={'Choose'}
                  optionFilterProp='children'
                  onChange={field.onChange}
                  value={field.value}
                  options={currencyList?.map((currency) => ({
                    value: currency,
                    label: currency,
                  }))}
                />
              )}
            />
          </Flex>
        </Grid>
      )}
    </>
  );
}
